import React, { Component } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Input, Select, Tabs, Badge, Tooltip, Avatar, Divider } from 'antd';
import {
	ExclamationCircleOutlined,
	CheckOutlined,
	CloseOutlined,
	EyeOutlined,
	WarningOutlined,
	UserOutlined,
	ClockCircleOutlined,
	FlagOutlined,
	MessageOutlined
} from '@ant-design/icons';
import './ContentModeration.scss';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;

class ContentModeration extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedReport: null,
			moderationModalVisible: false,
			moderationAction: '',
			moderationReason: '',
			activeTab: 'reports'
		};
	}

	// Safe array check helper
	safeArray = (arr) => {
		return Array.isArray(arr) ? arr : [];
	}

	// Format timestamp
	formatTimestamp = (timestamp) => {
		if (!timestamp) return 'Unknown';
		try {
			return new Date(timestamp).toLocaleString();
		} catch (error) {
			return 'Invalid Date';
		}
	}

	// Get report type tag
	getReportTypeTag = (type) => {
		if (!type) return <Tag color="default">Unknown</Tag>;

		const typeConfig = {
			inappropriate_content: { color: 'red', text: 'Inappropriate Content' },
			spam: { color: 'orange', text: 'Spam' },
			harassment: { color: 'volcano', text: 'Harassment' },
			copyright: { color: 'purple', text: 'Copyright' },
			violence: { color: 'red', text: 'Violence' },
			other: { color: 'default', text: 'Other' }
		};

		const config = typeConfig[type] || typeConfig.other;
		return <Tag color={config.color}>{config.text}</Tag>;
	}

	// Get priority tag
	getPriorityTag = (priority) => {
		if (!priority) return <Tag color="default">Normal</Tag>;

		const priorityConfig = {
			high: { color: 'red', text: 'High' },
			medium: { color: 'orange', text: 'Medium' },
			low: { color: 'green', text: 'Low' }
		};

		const config = priorityConfig[priority] || priorityConfig.low;
		return <Tag color={config.color}>{config.text}</Tag>;
	}

	// Get status tag
	getStatusTag = (status) => {
		if (!status) return <Tag color="default">Unknown</Tag>;

		const statusConfig = {
			pending: { color: 'processing', text: 'Pending' },
			approved: { color: 'success', text: 'Approved' },
			rejected: { color: 'error', text: 'Rejected' },
			resolved: { color: 'success', text: 'Resolved' }
		};

		const config = statusConfig[status] || statusConfig.pending;
		return <Tag color={config.color}>{config.text}</Tag>;
	}

	// Reports table columns
	getReportsColumns = () => {
		return [
			{
				title: 'Report ID',
				dataIndex: 'id',
				key: 'id',
				width: 80,
				render: (id) => `#${id || 'N/A'}`
			},
			{
				title: 'Stream',
				dataIndex: 'streamId',
				key: 'streamId',
				width: 100,
				render: (streamId) => (
					<Button 
						type="link" 
						size="small"
						onClick={() => this.viewStream(streamId)}
					>
						Stream #{streamId || 'N/A'}
					</Button>
				)
			},
			{
				title: 'Type',
				dataIndex: 'reportType',
				key: 'reportType',
				width: 150,
				render: (type) => this.getReportTypeTag(type)
			},
			{
				title: 'Description',
				dataIndex: 'description',
				key: 'description',
				width: 200,
				render: (text) => (
					<Tooltip title={text || 'No description'}>
						<span>{(text || 'No description').substring(0, 50)}...</span>
					</Tooltip>
				)
			},
			{
				title: 'Reported By',
				dataIndex: 'reportedBy',
				key: 'reportedBy',
				width: 120,
				render: (user) => (
					<Space>
						<Avatar size="small" icon={<UserOutlined />} />
						{user || 'Anonymous'}
					</Space>
				)
			},
			{
				title: 'Time',
				dataIndex: 'timestamp',
				key: 'timestamp',
				width: 150,
				render: (timestamp) => (
					<Space>
						<ClockCircleOutlined />
						{this.formatTimestamp(timestamp)}
					</Space>
				)
			},
			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status',
				width: 100,
				render: (status) => this.getStatusTag(status)
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 150,
				render: (_, record) => (
					<Space size="small">
						<Tooltip title="View Details">
							<Button 
								type="primary" 
								size="small" 
								icon={<EyeOutlined />}
								onClick={() => this.viewReportDetails(record)}
							/>
						</Tooltip>
						<Tooltip title="Approve">
							<Button 
								type="primary" 
								size="small" 
								icon={<CheckOutlined />}
								onClick={() => this.handleReportAction(record.id, 'approve')}
							/>
						</Tooltip>
						<Tooltip title="Reject">
							<Button 
								danger 
								size="small" 
								icon={<CloseOutlined />}
								onClick={() => this.handleReportAction(record.id, 'reject')}
							/>
						</Tooltip>
					</Space>
				)
			}
		];
	}

	// Moderation queue columns
	getModerationColumns = () => {
		return [
			{
				title: 'Queue ID',
				dataIndex: 'id',
				key: 'id',
				width: 80,
				render: (id) => `#${id || 'N/A'}`
			},
			{
				title: 'Stream',
				dataIndex: 'streamId',
				key: 'streamId',
				width: 100,
				render: (streamId) => (
					<Button 
						type="link" 
						size="small"
						onClick={() => this.viewStream(streamId)}
					>
						Stream #{streamId || 'N/A'}
					</Button>
				)
			},
			{
				title: 'Action Required',
				dataIndex: 'action',
				key: 'action',
				width: 150,
				render: (action) => (
					<Tag color="warning">{action || 'Review Required'}</Tag>
				)
			},
			{
				title: 'Priority',
				dataIndex: 'priority',
				key: 'priority',
				width: 100,
				render: (priority) => this.getPriorityTag(priority)
			},
			{
				title: 'Assigned To',
				dataIndex: 'assignedTo',
				key: 'assignedTo',
				width: 120,
				render: (assignee) => (
					<Space>
						<Avatar size="small" icon={<UserOutlined />} />
						{assignee || 'Unassigned'}
					</Space>
				)
			},
			{
				title: 'Created',
				dataIndex: 'timestamp',
				key: 'timestamp',
				width: 150,
				render: (timestamp) => (
					<Space>
						<ClockCircleOutlined />
						{this.formatTimestamp(timestamp)}
					</Space>
				)
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 200,
				render: (_, record) => (
					<Space size="small">
						<Button 
							type="primary" 
							size="small"
							onClick={() => this.handleModerationAction('approve', record.streamId)}
						>
							Approve
						</Button>
						<Button 
							size="small"
							onClick={() => this.handleModerationAction('warn', record.streamId)}
						>
							Warn
						</Button>
						<Button 
							danger 
							size="small"
							onClick={() => this.handleModerationAction('suspend', record.streamId)}
						>
							Suspend
						</Button>
					</Space>
				)
			}
		];
	}

	// View stream details
	viewStream = (streamId) => {
		console.log('Viewing stream:', streamId);
		// Add your stream viewing logic here
	}

	// View report details
	viewReportDetails = (report) => {
		this.setState({ 
			selectedReport: report,
			moderationModalVisible: true 
		});
	}

	// Handle report action
	handleReportAction = (reportId, action) => {
		const actionText = action === 'approve' ? 'approve' : 'reject';
		
		confirm({
			title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Report`,
			content: `Are you sure you want to ${actionText} this report?`,
			icon: <ExclamationCircleOutlined />,
			onOk: () => {
				if (this.props.onReportResolve) {
					this.props.onReportResolve(reportId, action);
				}
			}
		});
	}

	// Handle moderation action
	handleModerationAction = (action, streamId) => {
		if (this.props.onModerationAction) {
			this.props.onModerationAction(action, streamId);
		}
	}

	// Close moderation modal
	closeModerationModal = () => {
		this.setState({
			moderationModalVisible: false,
			selectedReport: null,
			moderationAction: '',
			moderationReason: ''
		});
	}

	render() {
		const { 
			reports = [], 
			moderationQueue = [], 
			loading = false 
		} = this.props;

		const {
			selectedReport,
			moderationModalVisible,
			activeTab
		} = this.state;

		// Ensure arrays are safe
		const safeReports = this.safeArray(reports).map(report => ({
			...report,
			key: report.id || Math.random()
		}));

		const safeModerationQueue = this.safeArray(moderationQueue).map(item => ({
			...item,
			key: item.id || Math.random()
		}));

		return (
			<div className="content-moderation">
				<div className="moderation-header">
					<div className="header-stats">
						<Card size="small" className="stat-card">
							<div className="stat-content">
								<FlagOutlined className="stat-icon" />
								<div>
									<div className="stat-number">{safeReports.length}</div>
									<div className="stat-label">Pending Reports</div>
								</div>
							</div>
						</Card>
						<Card size="small" className="stat-card">
							<div className="stat-content">
								<WarningOutlined className="stat-icon" />
								<div>
									<div className="stat-number">{safeModerationQueue.length}</div>
									<div className="stat-label">Moderation Queue</div>
								</div>
							</div>
						</Card>
					</div>
				</div>

				<Card>
					<Tabs 
						activeKey={activeTab}
						onChange={(key) => this.setState({ activeTab: key })}
					>
						<TabPane 
							tab={
								<span>
									<FlagOutlined />
									Reports
									{safeReports.length > 0 && (
										<Badge 
											count={safeReports.length} 
											size="small" 
											style={{ marginLeft: 8 }}
										/>
									)}
								</span>
							} 
							key="reports"
						>
							<Table
								columns={this.getReportsColumns()}
								dataSource={safeReports}
								loading={loading}
								pagination={{
									pageSize: 10,
									showSizeChanger: true,
									showTotal: (total, range) => 
										`${range[0]}-${range[1]} of ${total} reports`
								}}
								scroll={{ x: 1000 }}
							/>
						</TabPane>

						<TabPane 
							tab={
								<span>
									<WarningOutlined />
									Moderation Queue
									{safeModerationQueue.length > 0 && (
										<Badge 
											count={safeModerationQueue.length} 
											size="small" 
											style={{ marginLeft: 8 }}
										/>
									)}
								</span>
							} 
							key="queue"
						>
							<Table
								columns={this.getModerationColumns()}
								dataSource={safeModerationQueue}
								loading={loading}
								pagination={{
									pageSize: 10,
									showSizeChanger: true,
									showTotal: (total, range) => 
										`${range[0]}-${range[1]} of ${total} items`
								}}
								scroll={{ x: 1200 }}
							/>
						</TabPane>
					</Tabs>
				</Card>

				{/* Report Details Modal */}
				<Modal
					title="Report Details"
					visible={moderationModalVisible}
					onCancel={this.closeModerationModal}
					footer={[
						<Button key="cancel" onClick={this.closeModerationModal}>
							Cancel
						</Button>,
						<Button 
							key="approve" 
							type="primary"
							onClick={() => {
								this.handleReportAction(selectedReport?.id, 'approve');
								this.closeModerationModal();
							}}
						>
							Approve
						</Button>,
						<Button 
							key="reject" 
							danger
							onClick={() => {
								this.handleReportAction(selectedReport?.id, 'reject');
								this.closeModerationModal();
							}}
						>
							Reject
						</Button>
					]}
					width={600}
				>
					{selectedReport && (
						<div className="report-details">
							<div className="detail-section">
								<h4>Report Information</h4>
								<p><strong>Report ID:</strong> #{selectedReport.id}</p>
								<p><strong>Stream ID:</strong> #{selectedReport.streamId}</p>
								<p><strong>Type:</strong> {this.getReportTypeTag(selectedReport.reportType)}</p>
								<p><strong>Status:</strong> {this.getStatusTag(selectedReport.status)}</p>
							</div>
							
							<Divider />
							
							<div className="detail-section">
								<h4>Description</h4>
								<p>{selectedReport.description || 'No description provided'}</p>
							</div>
							
							<Divider />
							
							<div className="detail-section">
								<h4>Reporter Information</h4>
								<p><strong>Reported By:</strong> {selectedReport.reportedBy || 'Anonymous'}</p>
								<p><strong>Report Time:</strong> {this.formatTimestamp(selectedReport.timestamp)}</p>
							</div>
						</div>
					)}
				</Modal>
			</div>
		);
	}
}

export default ContentModeration;
